/**
 * Dynamic-block grip collection (pure math, no JSX).
 *
 * Split out of blockGrips.tsx to keep each module under the CLAUDE.md
 * size budget. Given an INSERT EntityPreview and its resolved
 * BlockDefinition, produce a flat list of BlockGrip records with
 * world-space coordinates.
 *
 * The axis-projection helper `projectDistance` lives here too because
 * it is consumed both by the grip renderer (to paint the live position
 * of the dragged secondPoint grip) and by the shell stage (to compute
 * the commit value on mouseup).
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";
import type {
  AlignedConstraintParameter,
  AlignmentParameter,
  AngularConstraintParameter,
  BlockDefinition,
  DiameterConstraintParameter,
  DynamicParameter,
  FlipParameter,
  HorizontalConstraintParameter,
  LinearParameter,
  LookupParameter,
  LookupTable,
  PointParameter,
  PolarParameter,
  RadialConstraintParameter,
  RotationParameter,
  VerticalConstraintParameter,
  VisibilityParameter,
  XYParameter,
} from "@dxf-ai-editor/drawing-model";
import { applyInstanceParameters } from "@dxf-ai-editor/drawing-model";

import { composeBlockMatrix, type BlockMatrix } from "./blockMatrix";
import { readNumber } from "./renderHelpers";
import { collectMinsertGrips } from "./blockGripCollector.minsert";
export {
  collectMinsertGrips,
  readMinsertGrid,
  resolveMinsertDragValue,
  readBlockCellSize,
} from "./blockGripCollector.minsert";

// `BlockGrip` + `BlockGripKind` live in `blockGripTypes.ts` so the
// MINSERT collector sibling can pull them in without re-importing this
// module (was a depcruise cycle source).
export type { BlockGrip, BlockGripKind } from "./blockGripTypes";

import type { BlockGrip } from "./blockGripTypes";

/* ── Transform helpers ────────────────────────────────────── */

function readInsertMatrix(
  entity: EntityPreview,
  blockDef: BlockDefinition,
): BlockMatrix {
  const props = entity.properties;
  let ix = Number(props["Insertion X"]);
  let iy = Number(props["Insertion Y"]);
  if (!Number.isFinite(ix) || !Number.isFinite(iy)) {
    const combined = props["Insertion point"];
    if (combined) {
      const parts = combined.split(",");
      const px = Number(parts[0]);
      const py = Number(parts[1]);
      if (!Number.isFinite(ix) && Number.isFinite(px)) ix = px;
      if (!Number.isFinite(iy) && Number.isFinite(py)) iy = py;
    }
  }
  let sx = Number(props["Scale X"]);
  let sy = Number(props["Scale Y"]);
  if (!Number.isFinite(sx) || !Number.isFinite(sy)) {
    const combined = props["Scale"];
    if (combined) {
      const parts = combined.split(",");
      const px = Number(parts[0]);
      const py = Number(parts[1]);
      if (!Number.isFinite(sx) && Number.isFinite(px)) sx = px;
      if (!Number.isFinite(sy) && Number.isFinite(py)) sy = py;
    }
  }
  if (!Number.isFinite(ix)) ix = 0;
  if (!Number.isFinite(iy)) iy = 0;
  if (!Number.isFinite(sx) || Math.abs(sx) < 1e-9) sx = 1;
  if (!Number.isFinite(sy) || Math.abs(sy) < 1e-9) sy = 1;

  return composeBlockMatrix({
    translate: { x: ix, y: iy },
    rotateDeg: readNumber(props, "Rotation", 0),
    scaleX: sx,
    scaleY: sy,
    basePoint: blockDef.basePoint,
  });
}

/**
 * Extract the per-INSERT customProperties map and visibilityState from
 * a preview entity's property bag. Keys populated by
 * `buildBlockInsertPreview` (dxfGeometry.ts) as `Param: <paramName>`
 * and `Visibility`.
 */
export function readInstanceOverrides(entity: EntityPreview): {
  customProperties: Record<string, string | number | boolean>;
  visibilityState?: string;
} {
  const customProperties: Record<string, string | number | boolean> = {};
  const prefix = "Param: ";
  for (const [key, value] of Object.entries(entity.properties)) {
    if (key.startsWith(prefix)) {
      customProperties[key.slice(prefix.length)] = value;
    }
  }
  const visibilityState = entity.properties["Visibility"];
  return {
    customProperties,
    visibilityState: visibilityState || undefined,
  };
}

/* ── Grip builders ────────────────────────────────────────── */

function buildLinearGrips(
  entityId: string,
  param: LinearParameter,
  matrix: BlockMatrix,
): BlockGrip[] {
  const base = matrix.transformPoint(param.basePoint);
  const dx = param.secondPoint.x - param.basePoint.x;
  const dy = param.secondPoint.y - param.basePoint.y;
  const authorLen = Math.hypot(dx, dy) || 1;
  const ux = dx / authorLen;
  const uy = dy / authorLen;
  const dist = Number.isFinite(param.currentDistance)
    ? param.currentDistance
    : authorLen;
  const liveSecondAuthor = {
    x: param.basePoint.x + ux * dist,
    y: param.basePoint.y + uy * dist,
  };
  const second = matrix.transformPoint(liveSecondAuthor);
  const worldDx = second.x - base.x;
  const worldDy = second.y - base.y;
  const axisAngleRad = Math.atan2(worldDy, worldDx);

  return [
    {
      id: `linear-${param.id}-base`,
      kind: "linear-base",
      entityId,
      parameterId: param.id,
      parameterName: param.name,
      x: base.x,
      y: base.y,
      cursor: "move",
      axisAngleRad,
      axisBaseWorld: { x: base.x, y: base.y },
    },
    {
      id: `linear-${param.id}-second`,
      kind: "linear-second",
      entityId,
      parameterId: param.id,
      parameterName: param.name,
      x: second.x,
      y: second.y,
      cursor: "move",
      axisAngleRad,
      axisBaseWorld: { x: base.x, y: base.y },
    },
  ];
}

function buildFlipGrip(
  entityId: string,
  param: FlipParameter,
  matrix: BlockMatrix,
): BlockGrip {
  const mid = {
    x: (param.axisStart.x + param.axisEnd.x) / 2,
    y: (param.axisStart.y + param.axisEnd.y) / 2,
  };
  const world = matrix.transformPoint(mid);
  return {
    id: `flip-${param.id}`,
    kind: "flip",
    entityId,
    parameterId: param.id,
    parameterName: param.name,
    x: world.x,
    y: world.y,
    cursor: "pointer",
  };
}

function buildVisibilityGrip(
  entityId: string,
  param: VisibilityParameter,
  matrix: BlockMatrix,
): BlockGrip {
  const world = matrix.transformPoint(param.position);
  return {
    id: `visibility-${param.id}`,
    kind: "visibility",
    entityId,
    parameterId: param.id,
    parameterName: param.name,
    x: world.x,
    y: world.y,
    cursor: "pointer",
    visibilityParameter: param,
  };
}

function buildBasePointGrip(
  entityId: string,
  param: { id: string; name: string; position: { x: number; y: number } },
  matrix: BlockMatrix,
): BlockGrip {
  const world = matrix.transformPoint(param.position);
  return {
    id: `basepoint-${param.id}`,
    kind: "basepoint",
    entityId,
    parameterId: param.id,
    parameterName: param.name,
    x: world.x,
    y: world.y,
    cursor: "move",
  };
}

function buildPointGrip(
  entityId: string,
  param: PointParameter,
  matrix: BlockMatrix,
): BlockGrip {
  const base = matrix.transformPoint(param.basePoint);
  const world = matrix.transformPoint(param.currentPoint);
  return {
    id: `point-${param.id}`,
    kind: "point",
    entityId,
    parameterId: param.id,
    parameterName: param.name,
    x: world.x,
    y: world.y,
    cursor: "move",
    paramOriginWorld: base,
  };
}

function buildPolarGrips(
  entityId: string,
  param: PolarParameter,
  matrix: BlockMatrix,
): BlockGrip[] {
  const base = matrix.transformPoint(param.basePoint);
  const angleRad = (param.currentAngle * Math.PI) / 180;
  const authorTip = {
    x: param.basePoint.x + param.currentDistance * Math.cos(angleRad),
    y: param.basePoint.y + param.currentDistance * Math.sin(angleRad),
  };
  const tip = matrix.transformPoint(authorTip);
  const worldAngleRad = Math.atan2(tip.y - base.y, tip.x - base.x);
  // Place angle grip on a circular arc at 0.6× distance so the two grips
  // don't overlap when the user zooms in.
  const arcRadiusAuthor = param.currentDistance * 0.6;
  const arcAngleAuthor = angleRad + Math.PI / 6;
  const arcAuthor = {
    x: param.basePoint.x + arcRadiusAuthor * Math.cos(arcAngleAuthor),
    y: param.basePoint.y + arcRadiusAuthor * Math.sin(arcAngleAuthor),
  };
  const arc = matrix.transformPoint(arcAuthor);
  return [
    {
      id: `polar-${param.id}-radial`,
      kind: "polar-radial",
      entityId,
      parameterId: param.id,
      parameterName: param.name,
      x: tip.x,
      y: tip.y,
      cursor: "move",
      axisAngleRad: worldAngleRad,
      axisBaseWorld: base,
      paramOriginWorld: base,
    },
    {
      id: `polar-${param.id}-angle`,
      kind: "polar-angle",
      entityId,
      parameterId: param.id,
      parameterName: param.name,
      x: arc.x,
      y: arc.y,
      cursor: "move",
      paramOriginWorld: base,
    },
  ];
}

function buildXYGrips(
  entityId: string,
  param: XYParameter,
  matrix: BlockMatrix,
): BlockGrip[] {
  const base = matrix.transformPoint(param.basePoint);
  const xHandleAuthor = {
    x: param.basePoint.x + param.currentX,
    y: param.basePoint.y,
  };
  const yHandleAuthor = {
    x: param.basePoint.x,
    y: param.basePoint.y + param.currentY,
  };
  const xHandle = matrix.transformPoint(xHandleAuthor);
  const yHandle = matrix.transformPoint(yHandleAuthor);
  return [
    {
      id: `xy-${param.id}-x`,
      kind: "xy-x",
      entityId,
      parameterId: param.id,
      parameterName: param.name,
      x: xHandle.x,
      y: xHandle.y,
      cursor: "move",
      paramOriginWorld: base,
    },
    {
      id: `xy-${param.id}-y`,
      kind: "xy-y",
      entityId,
      parameterId: param.id,
      parameterName: param.name,
      x: yHandle.x,
      y: yHandle.y,
      cursor: "move",
      paramOriginWorld: base,
    },
  ];
}

function buildRotationGrip(
  entityId: string,
  param: RotationParameter,
  matrix: BlockMatrix,
): BlockGrip {
  const base = matrix.transformPoint(param.basePoint);
  const angleRad = (param.currentAngle * Math.PI) / 180;
  const authorTip = {
    x: param.basePoint.x + param.radius * Math.cos(angleRad),
    y: param.basePoint.y + param.radius * Math.sin(angleRad),
  };
  const world = matrix.transformPoint(authorTip);
  const worldRadius = Math.hypot(world.x - base.x, world.y - base.y);
  return {
    id: `rotation-${param.id}`,
    kind: "rotation",
    entityId,
    parameterId: param.id,
    parameterName: param.name,
    x: world.x,
    y: world.y,
    cursor: "move",
    paramOriginWorld: base,
    rotationRadiusWorld: worldRadius,
  };
}

function buildHorizontalConstraintGrip(
  entityId: string,
  param: HorizontalConstraintParameter,
  matrix: BlockMatrix,
): BlockGrip {
  // Live tip = pointA + (currentDistance, 0). Stays on the X-axis of the
  // author space (subject to INSERT rotation/scale via matrix).
  const baseAuthor = param.pointA;
  const tipAuthor = {
    x: param.pointA.x + param.currentDistance,
    y: param.pointA.y,
  };
  const base = matrix.transformPoint(baseAuthor);
  const tip = matrix.transformPoint(tipAuthor);
  const axisAngleRad = Math.atan2(tip.y - base.y, tip.x - base.x);
  return {
    id: `constraint-h-${param.id}`,
    kind: "constraint-horizontal",
    entityId,
    parameterId: param.id,
    parameterName: param.name,
    x: tip.x,
    y: tip.y,
    cursor: "move",
    axisAngleRad,
    axisBaseWorld: base,
  };
}

function buildVerticalConstraintGrip(
  entityId: string,
  param: VerticalConstraintParameter,
  matrix: BlockMatrix,
): BlockGrip {
  const baseAuthor = param.pointA;
  const tipAuthor = {
    x: param.pointA.x,
    y: param.pointA.y + param.currentDistance,
  };
  const base = matrix.transformPoint(baseAuthor);
  const tip = matrix.transformPoint(tipAuthor);
  const axisAngleRad = Math.atan2(tip.y - base.y, tip.x - base.x);
  return {
    id: `constraint-v-${param.id}`,
    kind: "constraint-vertical",
    entityId,
    parameterId: param.id,
    parameterName: param.name,
    x: tip.x,
    y: tip.y,
    cursor: "move",
    axisAngleRad,
    axisBaseWorld: base,
  };
}

function buildAngularConstraintGrip(
  entityId: string,
  param: AngularConstraintParameter,
  matrix: BlockMatrix,
): BlockGrip {
  // Place the angle grip on a circular arc at radius = ½ · |vertex→pointB|
  // around the vertex, at the current angle. Drag updates the angle.
  const vertex = matrix.transformPoint(param.vertex);
  const refDx = param.pointB.x - param.vertex.x;
  const refDy = param.pointB.y - param.vertex.y;
  const radiusAuthor = Math.hypot(refDx, refDy) * 0.5 || 50;
  const angleRad = (param.currentAngle * Math.PI) / 180;
  const tipAuthor = {
    x: param.vertex.x + radiusAuthor * Math.cos(angleRad),
    y: param.vertex.y + radiusAuthor * Math.sin(angleRad),
  };
  const tip = matrix.transformPoint(tipAuthor);
  const worldRadius = Math.hypot(tip.x - vertex.x, tip.y - vertex.y);
  return {
    id: `constraint-a-${param.id}`,
    kind: "constraint-angular",
    entityId,
    parameterId: param.id,
    parameterName: param.name,
    x: tip.x,
    y: tip.y,
    cursor: "move",
    paramOriginWorld: vertex,
    rotationRadiusWorld: worldRadius,
  };
}

function buildAlignmentGrip(
  entityId: string,
  param: AlignmentParameter,
  matrix: BlockMatrix,
): BlockGrip {
  // Alignment grip (DYN-3 Tier 3). Rhombus (parallelogram) rendered at
  // the parameter's authored `position`, rotating with `currentAngle`.
  // Dragging the grip updates the angle (degrees) that the evaluator
  // applies as an extra rotation around `position`.
  const base = matrix.transformPoint(param.position);
  const angleRad = (param.currentAngle * Math.PI) / 180;
  // Place grip tip at a nominal radius (author space 40 units scaled
  // through the INSERT matrix) — gives a visible handle the user can
  // grab to rotate the alignment axis.
  const radiusAuthor = 40;
  const tipAuthor = {
    x: param.position.x + radiusAuthor * Math.cos(angleRad),
    y: param.position.y + radiusAuthor * Math.sin(angleRad),
  };
  const tip = matrix.transformPoint(tipAuthor);
  const worldRadius = Math.hypot(tip.x - base.x, tip.y - base.y);
  return {
    id: `alignment-${param.id}`,
    kind: "alignment",
    entityId,
    parameterId: param.id,
    parameterName: param.name,
    x: tip.x,
    y: tip.y,
    cursor: "move",
    paramOriginWorld: base,
    rotationRadiusWorld: worldRadius,
  };
}

function buildAlignedConstraintGrip(
  entityId: string,
  param: AlignedConstraintParameter,
  matrix: BlockMatrix,
): BlockGrip {
  // Aligned dimensional constraint (DYN-3 Tier 3). Axis = authored
  // direction pointA → pointB. The grip slides along that axis and
  // updates `currentDistance`.
  const baseAuthor = param.pointA;
  const authorDx = param.pointB.x - param.pointA.x;
  const authorDy = param.pointB.y - param.pointA.y;
  const authorLen = Math.hypot(authorDx, authorDy) || 1;
  const ux = authorDx / authorLen;
  const uy = authorDy / authorLen;
  const tipAuthor = {
    x: baseAuthor.x + ux * param.currentDistance,
    y: baseAuthor.y + uy * param.currentDistance,
  };
  const base = matrix.transformPoint(baseAuthor);
  const tip = matrix.transformPoint(tipAuthor);
  const axisAngleRad = Math.atan2(tip.y - base.y, tip.x - base.x);
  return {
    id: `constraint-aligned-${param.id}`,
    kind: "constraint-aligned",
    entityId,
    parameterId: param.id,
    parameterName: param.name,
    x: tip.x,
    y: tip.y,
    cursor: "move",
    axisAngleRad,
    axisBaseWorld: base,
  };
}

function buildRadialConstraintGrip(
  entityId: string,
  param: RadialConstraintParameter,
  matrix: BlockMatrix,
): BlockGrip {
  // Radial dimensional constraint (DYN-3 Tier 3). Axis = center →
  // radiusPoint direction. Distance drag = radius.
  const baseAuthor = param.center;
  const authorDx = param.radiusPoint.x - param.center.x;
  const authorDy = param.radiusPoint.y - param.center.y;
  const authorLen = Math.hypot(authorDx, authorDy) || 1;
  const ux = authorDx / authorLen;
  const uy = authorDy / authorLen;
  const tipAuthor = {
    x: baseAuthor.x + ux * param.currentRadius,
    y: baseAuthor.y + uy * param.currentRadius,
  };
  const base = matrix.transformPoint(baseAuthor);
  const tip = matrix.transformPoint(tipAuthor);
  const axisAngleRad = Math.atan2(tip.y - base.y, tip.x - base.x);
  return {
    id: `constraint-radial-${param.id}`,
    kind: "constraint-radial",
    entityId,
    parameterId: param.id,
    parameterName: param.name,
    x: tip.x,
    y: tip.y,
    cursor: "move",
    axisAngleRad,
    axisBaseWorld: base,
  };
}

function buildDiameterConstraintGrip(
  entityId: string,
  param: DiameterConstraintParameter,
  matrix: BlockMatrix,
): BlockGrip {
  // Diameter dimensional constraint (DYN-3 Tier 3). Axis = point1 →
  // point2 direction through center. Distance drag = diameter.
  const baseAuthor = param.point1;
  const authorDx = param.point2.x - param.point1.x;
  const authorDy = param.point2.y - param.point1.y;
  const authorLen = Math.hypot(authorDx, authorDy) || 1;
  const ux = authorDx / authorLen;
  const uy = authorDy / authorLen;
  const tipAuthor = {
    x: baseAuthor.x + ux * param.currentDiameter,
    y: baseAuthor.y + uy * param.currentDiameter,
  };
  const base = matrix.transformPoint(baseAuthor);
  const tip = matrix.transformPoint(tipAuthor);
  const axisAngleRad = Math.atan2(tip.y - base.y, tip.x - base.x);
  return {
    id: `constraint-diameter-${param.id}`,
    kind: "constraint-diameter",
    entityId,
    parameterId: param.id,
    parameterName: param.name,
    x: tip.x,
    y: tip.y,
    cursor: "move",
    axisAngleRad,
    axisBaseWorld: base,
  };
}

function buildLookupGrip(
  entityId: string,
  param: LookupParameter,
  lookupTables: readonly LookupTable[],
  matrix: BlockMatrix,
): BlockGrip {
  const world = matrix.transformPoint(param.position);
  const table = lookupTables.find((tbl) => tbl.id === param.lookupTableId);
  return {
    id: `lookup-${param.id}`,
    kind: "lookup",
    entityId,
    parameterId: param.id,
    parameterName: param.name,
    x: world.x,
    y: world.y,
    cursor: "pointer",
    lookupParameter: param,
    lookupTable: table,
  };
}

/* ── Public: collectBlockGrips ──────────────────────────── */

export function collectBlockGrips(
  entity: EntityPreview,
  blockDef: BlockDefinition,
): BlockGrip[] {
  const dyn = blockDef.dynamicMetadata;
  const grips: BlockGrip[] = [];

  // FIN.7 — MINSERT grips are authoring-state-independent; emit them
  // whenever the INSERT has a multi-cell grid, regardless of whether the
  // block itself is dynamic.
  grips.push(...collectMinsertGrips(entity, blockDef));

  if (!dyn) return grips;

  const overrides = readInstanceOverrides(entity);
  const liveMeta = applyInstanceParameters(
    dyn,
    overrides.customProperties,
    overrides.visibilityState,
  );
  const matrix = readInsertMatrix(entity, blockDef);

  for (const param of liveMeta.parameters) {
    if (param.showGrip === false) continue;
    switch (param.kind) {
      case "linear":
        grips.push(...buildLinearGrips(entity.id, param, matrix));
        break;
      case "flip":
        grips.push(buildFlipGrip(entity.id, param, matrix));
        break;
      case "visibility":
        grips.push(buildVisibilityGrip(entity.id, param, matrix));
        break;
      case "basepoint":
        grips.push(buildBasePointGrip(entity.id, param, matrix));
        break;
      case "point":
        grips.push(buildPointGrip(entity.id, param, matrix));
        break;
      case "polar":
        grips.push(...buildPolarGrips(entity.id, param, matrix));
        break;
      case "xy":
        grips.push(...buildXYGrips(entity.id, param, matrix));
        break;
      case "rotation":
        grips.push(buildRotationGrip(entity.id, param, matrix));
        break;
      case "lookup":
        grips.push(
          buildLookupGrip(
            entity.id,
            param,
            liveMeta.lookupTables ?? [],
            matrix,
          ),
        );
        break;
      case "constraint-horizontal":
        grips.push(buildHorizontalConstraintGrip(entity.id, param, matrix));
        break;
      case "constraint-vertical":
        grips.push(buildVerticalConstraintGrip(entity.id, param, matrix));
        break;
      case "constraint-angular":
        grips.push(buildAngularConstraintGrip(entity.id, param, matrix));
        break;
      case "alignment":
        grips.push(buildAlignmentGrip(entity.id, param, matrix));
        break;
      case "constraint-aligned":
        grips.push(buildAlignedConstraintGrip(entity.id, param, matrix));
        break;
      case "constraint-radial":
        grips.push(buildRadialConstraintGrip(entity.id, param, matrix));
        break;
      case "constraint-diameter":
        grips.push(buildDiameterConstraintGrip(entity.id, param, matrix));
        break;
      case "user":
        // DYN-4 Fix 3: User parameter is a pure property-palette value
        // (ACDBASSOCVARIABLE-backed). It has no spatial grip — it is
        // edited via DYNSET / the Block Properties dialog, not by
        // dragging in the drawing canvas.
        break;
      default: {
        // Exhaustiveness — new parameter kinds add a case above.
        const _never: never = param;
        void _never;
        break;
      }
    }
  }

  return grips;
}

/* ── Axis projection helper ─────────────────────────────── */

/**
 * Project a world-space mouse point onto a linear axis; return the
 * signed distance along the axis direction from `base`.
 */
export function projectDistance(
  base: { x: number; y: number },
  axisAngleRad: number,
  mouseWorld: { x: number; y: number },
): number {
  const dx = mouseWorld.x - base.x;
  const dy = mouseWorld.y - base.y;
  return dx * Math.cos(axisAngleRad) + dy * Math.sin(axisAngleRad);
}

/* ── Parameter lookup helper (exported for tests / palette) ─ */

export function findDynamicParameterByName(
  blockDef: BlockDefinition,
  name: string,
): DynamicParameter | undefined {
  return blockDef.dynamicMetadata?.parameters.find((p) => p.name === name);
}
